import { useCallback, useEffect, useState } from "react";
import { StrategyCatalogueEntry, WindowRecommendation, api, isError } from "../api";

/** Multi-select for a list of strategy ids, fed by GET
 *  /convergence/strategies so the operator picks from strategies that
 *  exist instead of typing ids.
 *
 *  variant "convergence" (`convergence.enabled_strategies`): favourites,
 *  built-ins, backtested and seen-live ids. Any number of picks (1+ =
 *  strict mode with exactly those ids; none = single-fire). A custom id can
 *  still be added for an alert that hasn't fired yet.
 *
 *  variant "local" (`signals.local_strategies`): favourites only, since
 *  the local runner rebuilds each strategy from the run it was validated on
 *  and anything unvalidated is skipped. "Add all favourites" is one click,
 *  but deliberately not automatic: everything in this list trades once
 *  the runner is on, so starring a backtest must not start trading it. */
export function StrategyPicker({
  enabled,
  onSave,
  onSetWindow,
  busy,
  variant = "convergence",
}: {
  enabled: string[];
  onSave: (ids: string[]) => Promise<void>;
  /** Writes convergence.window_seconds; used to auto-apply the recommendation. */
  onSetWindow?: (seconds: number) => Promise<void>;
  busy: boolean;
  variant?: "convergence" | "local";
}) {
  const local = variant === "local";
  const [catalogue, setCatalogue] = useState<StrategyCatalogueEntry[] | null>(null);
  const [recommended, setRecommended] = useState<WindowRecommendation | null>(null);
  const [currentWindow, setCurrentWindow] = useState<number | null>(null);
  const [windowNote, setWindowNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [custom, setCustom] = useState("");
  const [showOthers, setShowOthers] = useState(false);

  const refresh = useCallback(async () => {
    const r = await api.strategyCatalogue();
    if (isError(r)) {
      setError(r.error);
      return null;
    }
    setError(null);
    setCatalogue(r.strategies);
    setRecommended(r.recommended_window);
    setCurrentWindow(r.current_window);
    return r;
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh, enabled]);

  // After the selection changes, re-read the recommendation and apply it.
  // The window is derived from the selected strategies' timeframes, so a
  // new selection usually implies a new window; the operator can still
  // edit the Convergence window row afterwards to override.
  async function commit(next: string[]) {
    await onSave(next);
    if (!onSetWindow) return;
    const r = await api.strategyCatalogue();
    if (isError(r) || !r.recommended_window) return;
    const rec = r.recommended_window;
    if (rec.window_seconds !== r.current_window) {
      await onSetWindow(rec.window_seconds);
      setWindowNote(`Window set to ${rec.window_seconds} s (${rec.basis}).`);
    }
    await refresh();
  }

  async function toggle(name: string) {
    const next = enabled.includes(name)
      ? enabled.filter((n) => n !== name)
      : [...enabled, name];
    await commit(next);
  }

  async function addAllFavourites() {
    const favs = (catalogue ?? []).filter((s) => s.favourite !== null).map((s) => s.name);
    const next = [...enabled, ...favs.filter((n) => !enabled.includes(n))];
    if (next.length !== enabled.length) await commit(next);
  }

  async function addCustom() {
    const id = custom.trim();
    if (!id) return;
    if (!/^[A-Za-z0-9_.-]{1,64}$/.test(id)) {
      setError("ids may only contain letters, digits, _ . - (max 64)");
      return;
    }
    setCustom("");
    if (!enabled.includes(id)) await commit([...enabled, id]);
  }

  return (
    <div className="w-full max-w-xl">
      {error && (
        <p className="mb-2 rounded-md border border-danger/40 bg-danger/10 px-2 py-1 text-xs text-danger">
          {error}
        </p>
      )}
      {catalogue === null ? (
        <p className="text-xs text-fg-muted">Loading strategies…</p>
      ) : (
        <>
          <Section
            title="Favourites"
            hint="saved on Backtest → ★ favourites"
            rows={catalogue.filter((s) => s.favourite !== null)}
            empty="No favourites yet - save a tested strategy on the Backtest page."
            enabled={enabled}
            busy={busy}
            onToggle={toggle}
          />
          {local && (() => {
            const missing = catalogue.filter(
              (s) => s.favourite !== null && !enabled.includes(s.name),
            ).length;
            return (
              <button
                type="button"
                onClick={() => void addAllFavourites()}
                disabled={busy || missing === 0}
                className="mt-2 rounded-md border border-border px-2 py-1 text-[10px] uppercase tracking-wide text-fg-muted hover:bg-bg-hover hover:text-fg disabled:opacity-50"
              >
                {missing === 0 ? "all favourites added" : `add all favourites (${missing})`}
              </button>
            );
          })()}
          {(() => {
            // Anything not a favourite but still selected must stay visible,
            // or the operator could not untick it. `enabled` is this picker's
            // own selection (the catalogue's `enabled` flag is convergence's).
            const others = catalogue.filter((s) => s.favourite === null);
            const selected = others.filter((s) => enabled.includes(s.name));
            // Ids selected but unknown to the catalogue (e.g. a typo, or a
            // favourite since deleted) must stay removable too.
            const unknown = enabled
              .filter((n) => !catalogue.some((s) => s.name === n))
              .map((n) => ({ ...unknownRow, name: n }));
            const visible = [
              ...(local ? selected : showOthers ? others : selected),
              ...unknown,
            ];
            return (
              <div className="mt-2">
                {!local && (
                  <button
                    type="button"
                    onClick={() => setShowOthers((v) => !v)}
                    className="text-[10px] uppercase tracking-wide text-fg-muted hover:text-fg"
                  >
                    {showOthers ? "hide" : "show"} other known ids ({others.length})
                  </button>
                )}
                {local && visible.length > 0 && (
                  <p className="mb-1 text-[10px] text-warn">
                    Not a favourite: the local runner skips these unless they have a
                    completed backtest to rebuild from.
                  </p>
                )}
                {visible.length > 0 && (
                  <Section
                    title=""
                    hint=""
                    rows={visible}
                    empty=""
                    enabled={enabled}
                    busy={busy}
                    onToggle={toggle}
                  />
                )}
              </div>
            );
          })()}
        </>
      )}
      {!local && (
      <div className="mt-2 flex items-center gap-2">
        <input
          type="text"
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void addCustom();
            }
          }}
          placeholder="custom strategy_id (for an alert you haven't sent yet)"
          className="min-w-0 flex-1 rounded-md border border-border bg-page px-2 py-1 font-mono text-xs text-fg outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
          disabled={busy}
        />
        <button
          type="button"
          onClick={() => void addCustom()}
          disabled={busy || !custom.trim()}
          className="rounded-md border border-border px-2 py-1 text-[10px] uppercase tracking-wide text-fg-muted hover:bg-bg-hover hover:text-fg disabled:opacity-50"
        >
          add
        </button>
      </div>
      )}
      {local ? (
        <p className="mt-2 text-xs text-fg-muted">
          {enabled.length === 0
            ? "None selected: the local runner evaluates nothing."
            : `${enabled.length} evaluated each cycle, each on the symbol and timeframe it was validated on. Only runs while the Local signal runner toggle (Settings) is on.`}
        </p>
      ) : (
      <p className="mt-2 text-xs text-fg-muted">
        {enabled.length === 0
          ? "None selected: single-fire mode, every signal executes on its own."
          : enabled.length === 1
            ? `Strict mode with one strategy: only ${enabled[0]} can trade; every other id is ignored.`
            : `Strict mode: all ${enabled.length} must signal the same direction within the window.`}
      </p>
      )}
      {!local && enabled.length > 0 && (
        <p className="mt-1 text-xs text-fg-muted">
          {windowNote ??
            (recommended
              ? recommended.window_seconds === currentWindow
                ? `Window ${currentWindow} s matches the recommendation (${recommended.basis}).`
                : `Recommended window ${recommended.window_seconds} s (${recommended.basis}); current ${currentWindow ?? "?"} s.`
              : "No recommended window: none of the selected strategies has a backtested timeframe yet.")}
          {recommended && recommended.window_seconds !== currentWindow && onSetWindow && (
            <>
              {" "}
              <button
                type="button"
                onClick={() =>
                  void (async () => {
                    await onSetWindow(recommended.window_seconds);
                    setWindowNote(`Window set to ${recommended.window_seconds} s (${recommended.basis}).`);
                    await refresh();
                  })()
                }
                disabled={busy}
                className="rounded border border-border px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-fg-muted hover:bg-bg-hover hover:text-fg disabled:opacity-50"
              >
                apply
              </button>
            </>
          )}
        </p>
      )}
    </div>
  );
}

// Stand-in catalogue row for a selected id the catalogue does not know.
const unknownRow: StrategyCatalogueEntry = {
  name: "",
  sources: [],
  builtin_label: null,
  favourite: null,
  backtest: null,
  signals: null,
  enabled: false,
};

function Section({
  title,
  hint,
  rows,
  empty,
  enabled,
  busy,
  onToggle,
}: {
  title: string;
  hint: string;
  rows: StrategyCatalogueEntry[];
  empty: string;
  enabled: string[];
  busy: boolean;
  onToggle: (name: string) => Promise<void>;
}) {
  return (
    <div>
      {title && (
        <div className="mb-1 flex items-baseline gap-2">
          <span className="text-[10px] font-bold uppercase tracking-wide text-fg-muted">{title}</span>
          {hint && <span className="text-[10px] text-fg-muted">{hint}</span>}
        </div>
      )}
      {rows.length === 0 ? (
        empty ? <p className="text-xs text-fg-muted">{empty}</p> : null
      ) : (
        <ul className="divide-y divide-border rounded-md border border-border">
          {rows.map((s) => (
            <li key={s.name} className="flex items-start gap-3 px-3 py-2">
              <input
                id={`sp-${s.name}`}
                type="checkbox"
                checked={enabled.includes(s.name)}
                onChange={() => void onToggle(s.name)}
                disabled={busy}
                className="mt-1"
              />
              <label htmlFor={`sp-${s.name}`} className="min-w-0 flex-1 cursor-pointer">
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="font-mono text-sm text-fg">
                    {s.favourite ? "★ " : ""}
                    {s.name}
                  </span>
                  {s.favourite ? (
                    <span className="text-xs text-fg-muted">{s.favourite.strategy_name}</span>
                  ) : (
                    s.builtin_label && <span className="text-xs text-fg-muted">{s.builtin_label}</span>
                  )}
                  {s.favourite ? (
                    <Badge tone="ok">favourite</Badge>
                  ) : s.sources.includes("backtest") ? (
                    <Badge tone="ok">tested</Badge>
                  ) : null}
                  {s.sources.includes("signals") && <Badge tone="info">live alerts</Badge>}
                  {!s.favourite && !s.sources.includes("backtest") && !s.sources.includes("signals") && (
                    <Badge tone="muted">untested</Badge>
                  )}
                </div>
                <p className="mt-0.5 text-xs text-fg-muted">{evidence(s)}</p>
              </label>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function evidence(s: StrategyCatalogueEntry): string {
  const parts: string[] = [];
  if (s.favourite) {
    const f = s.favourite;
    const pf = f.profit_factor != null ? `PF ${f.profit_factor.toFixed(2)}` : "PF n/a";
    const exp = f.expectancy != null ? `${f.expectancy >= 0 ? "+" : ""}${f.expectancy.toFixed(1)} pips/trade` : "";
    parts.push(
      `validated ${f.symbol ?? "?"} ${f.timeframe ?? ""}: ${f.trades ?? "?"} trades, ${pf}${exp ? `, ${exp}` : ""}`,
    );
    if (f.note) parts.push(f.note);
  }
  const bt = s.backtest;
  if (bt) {
    if (bt.latest) {
      const l = bt.latest;
      const pf = l.profit_factor != null ? `PF ${l.profit_factor.toFixed(2)}` : "PF n/a";
      const exp = l.expectancy != null ? `${l.expectancy >= 0 ? "+" : ""}${l.expectancy.toFixed(1)} pips/trade` : "";
      parts.push(
        `latest ${l.symbol} ${l.timeframe}: ${l.trades ?? "?"} trades, ${pf}${exp ? `, ${exp}` : ""}`,
      );
    }
    if (bt.runs > 1 && bt.best_profit_factor != null) {
      parts.push(`best PF ${bt.best_profit_factor.toFixed(2)} over ${bt.runs} runs`);
    }
    if (bt.convergence_runs > 0) {
      parts.push(`in ${bt.convergence_runs} convergence run${bt.convergence_runs === 1 ? "" : "s"}`);
    }
  }
  if (s.signals) {
    const when = s.signals.last_seen ? new Date(s.signals.last_seen).toLocaleString() : "";
    parts.push(`${s.signals.count} alert${s.signals.count === 1 ? "" : "s"} received${when ? `, last ${when}` : ""}`);
  }
  if (parts.length === 0) {
    return s.sources.includes("builtin")
      ? "No backtest yet - run one on the Backtest page before enabling."
      : "Only known from the enabled list.";
  }
  return parts.join(" · ");
}

function Badge({ tone, children }: { tone: "ok" | "info" | "muted"; children: string }) {
  const cls =
    tone === "ok"
      ? "bg-success/20 text-success"
      : tone === "info"
        ? "bg-primary/15 text-primary"
        : "bg-bg-hover text-fg-muted";
  return (
    <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${cls}`}>
      {children}
    </span>
  );
}
